import { environment } from "@/config/env";
import { ArrowRight, Mars, Venus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CitySelect } from "../city-select";
import { CountrySelect } from "../country-select";
import { DatePicker } from "../date-picker";
import type { GuestDataProps } from "../GuestForm";
import LoadingItem from "../LoadingItem";
import { ProvinceSelect } from "../province-select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export default function Anagraphic({
  data,
  loading,
  onChange,
  onError,
  onNext,
}: {
  data: GuestDataProps;
  loading?: boolean;
  onChange: (data: GuestDataProps) => void;
  onError?: (errors: string[]) => void;
  onNext?: () => void;
}) {
  const { t } = useTranslation("guestForm");
  const { t: tCommon } = useTranslation();
  const [errors, setErrors] = useState<string[]>([]);

  const handleChange = (field: keyof GuestDataProps, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const getErrorMessage = (errorCode: string): string => {
    return t(`errors.${errorCode}`, t("errors.required"));
  };

  const hasError = (errorCode: string): boolean => {
    return errors.includes(errorCode);
  };
  const isFormValid = () => {
    // Campi obbligatori base
    if (!data.name || data.name.trim() === "") return false;
    if (!data.surname || data.surname.trim() === "") return false;
    if (!data.birthDate) return false;
    if (data.gender === undefined || data.gender === null) return false;
    if (!data.email || data.email.trim() === "") return false;
    if (!data.birthCountry) return false;
    if (
      !data.birthCity ||
      !data.birthCity.name ||
      data.birthCity.name.trim() === ""
    )
      return false;

    // Validazioni per nascita italiana
    if (data.birthCountry?.iso2?.toUpperCase() === "IT") {
      if (!data.birthProvince || data.birthProvince.code.trim() === "")
        return false;
    }

    // Campi obbligatori di residenza
    if (!data.residenceCountry) return false;
    if (
      !data.residenceCity ||
      !data.residenceCity.name ||
      data.residenceCity.name.trim() === ""
    )
      return false;
    if (!data.residenceAddress || data.residenceAddress.trim() === "")
      return false;

    // Validazioni per residenza italiana
    if (data.residenceCountry?.iso2?.toUpperCase() === "IT") {
      if (!data.residenceProvince || data.residenceProvince.code.trim() === "")
        return false;

      if (!data.cap || data.cap.trim() === "") return false;
      // Se non è italiana per nascita ma è italiana per residenza, serve codice fiscale
      if (data.birthCountry?.iso2?.toUpperCase() !== "IT") {
        if (!data.taxCode || data.taxCode.trim() === "") return false;
      }
    }

    return true;
  };

  useEffect(() => {
    const errors: string[] = [];
    if (!data.name || data.name.trim() === "") {
      errors.push("required_name");
    }
    if (!data.surname || data.surname.trim() === "") {
      errors.push("required_surname");
    }
    if (!data.birthDate) {
      errors.push("required_birth_date");
    }
    if (data.gender === undefined || data.gender === null) {
      errors.push("required_gender");
    }
    if (!data.email || data.email.trim() === "") {
      errors.push("required_email");
    }
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.push("invalid_email");
    }
    if (!data.birthCountry?.iso2) {
      errors.push("required_birth_country");
    }
    if (
      !data.birthCity ||
      !data.birthCity.name ||
      data.birthCity.name.trim() === ""
    ) {
      errors.push("required_birth_city");
    }
    if (data.birthCountry?.iso2?.toUpperCase() === "IT") {
      if (!data.birthProvince || data.birthProvince.code.trim() === "") {
        errors.push("required_birth_province");
      }
      if (!data.taxCode || data.taxCode.trim() === "") {
        errors.push("required_tax_code");
      }
    }
    if (!data.residenceCountry) {
      errors.push("required_residence_country");
    }
    if (
      !data.residenceCity ||
      !data.residenceCity.name ||
      data.residenceCity.name.trim() === ""
    ) {
      errors.push("required_residence_city");
    }
    if (!data.residenceAddress || data.residenceAddress.trim() === "") {
      errors.push("required_residence_address");
    }
    if (data.residenceCountry?.iso2?.toUpperCase() === "IT") {
      if (
        !data.residenceProvince ||
        data.residenceProvince.name.trim() === ""
      ) {
        errors.push("required_residence_province");
      }

      if (!data.cap || data.cap.trim() === "") {
        errors.push("required_postal_code");
      }
      if (data.birthCountry?.iso2?.toUpperCase() !== "IT") {
        if (!data.taxCode || data.taxCode.trim() === "") {
          errors.push("required_tax_code");
        }
      }
    }
    setErrors(errors);
    onError?.(errors);
  }, [data]);
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && (
          <LoadingItem text={t("loading_item.extraction_in_progress")} />
        )}
        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.name")}</Label>
          <Input
            disabled={loading}
            value={data.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder={t("placeholders.name")}
            className={hasError("required_name") ? "border-red-500" : ""}
          />
          {hasError("required_name") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("required_name")}
            </p>
          )}
        </div>
        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.surname")}</Label>
          <Input
            disabled={loading}
            value={data.surname}
            onChange={(e) => handleChange("surname", e.target.value)}
            placeholder={t("placeholders.surname")}
            className={hasError("required_surname") ? "border-red-500" : ""}
          />
          {hasError("required_surname") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("required_surname")}
            </p>
          )}
        </div>

        {/* Email e Cellulare */}
        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.email")}</Label>
          <Input
            disabled={loading}
            type="email"
            value={data.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder={t("placeholders.email")}
            className={
              hasError("required_email") || hasError("invalid_email")
                ? "border-red-500"
                : ""
            }
          />
          {hasError("required_email") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("required_email")}
            </p>
          )}
          {hasError("invalid_email") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("invalid_email")}
            </p>
          )}
        </div>
        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.phone")}</Label>
          <Input
            disabled={loading}
            value={data.phoneNumber}
            onChange={(e) => handleChange("phoneNumber", e.target.value)}
            placeholder={t("placeholders.phone")}
          />
        </div>

        {/* Data di Nascita e Genere */}
        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.birthDate")}</Label>
          <DatePicker
            disabled={loading}
            className={`w-full ${hasError("required_birth_date") ? "border-red-500" : ""}`}
            value={data?.birthDate ?? undefined}
            onChange={(date) => handleChange("birthDate", date)}
            placeholder={tCommon(
              "documentVerification.placeholders.datePlaceholder",
            )}
          />
          {hasError("required_birth_date") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("required_birth_date")}
            </p>
          )}
        </div>

        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.gender")}</Label>
          <Select
            disabled={loading}
            onValueChange={(v) => handleChange("gender", parseInt(v))}
            value={data.gender?.toString()}
          >
            <SelectTrigger
              className={`w-full ${hasError("required_gender") ? "border-red-500" : ""}`}
            >
              <SelectValue placeholder={t("anagraphic.select")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">
                <Mars />
                {t("gender.male")}
              </SelectItem>
              <SelectItem value="1">
                <Venus />
                {t("gender.female")}
              </SelectItem>
              <SelectItem value="2">{t("gender.other")}</SelectItem>
            </SelectContent>
          </Select>
          {hasError("required_gender") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("required_gender")}
            </p>
          )}
        </div>

        {/* Luogo di Nascita */}
        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.birthCountry")}</Label>
          <div
            className={
              hasError("required_birth_country")
                ? "border border-red-500 rounded-md"
                : ""
            }
          >
            <CountrySelect
              disabled={loading}
              value={data.birthCountry}
              apiEndpoint={`${environment.ELITE_BASE_URI}/geo/countries`}
              onValueChange={(value) => handleChange("birthCountry", value)}
              placeholder={t("placeholders.birthCountry")}
            />
          </div>
          {hasError("required_birth_country") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("required_birth_country")}
            </p>
          )}
        </div>

        {/* Provincia di Nascita (Condizionale per Italiani) */}
        {data.birthCountry?.iso2?.toUpperCase() === "IT" && (
          <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
            <Label>{t("anagraphic.birthProvince")}</Label>
            <div
              className={
                hasError("required_birth_province")
                  ? "border border-red-500 rounded-md"
                  : ""
              }
            >
              <ProvinceSelect
                disabled={loading}
                value={data.birthProvince}
                onValueChange={(value) => handleChange("birthProvince", value)}
                placeholder={t("placeholders.birthProvince")}
              />
            </div>
            {hasError("required_birth_province") && (
              <p className="text-sm text-red-500">
                {getErrorMessage("required_birth_province")}
              </p>
            )}
          </div>
        )}

        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.birthCity")}</Label>
          <div
            className={
              hasError("required_birth_city")
                ? "border border-red-500 rounded-md"
                : ""
            }
          >
            <CitySelect
              value={data.birthCity}
              apiEndpoint={`${environment.ELITE_BASE_URI}/geo/cities`}
              disabled={loading || !data?.birthCountry?.iso2}
              nationId={data.birthCountry?.id}
              onValueChange={(value) => handleChange("birthCity", value)}
              placeholder={t("placeholders.birthCity")}
            />
          </div>
          {hasError("required_birth_city") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("required_birth_city")}
            </p>
          )}
        </div>

        {/* Separator Dati Residenza */}
        <div className="col-span-2 mt-6 mb-2">
          <h3 className="text-lg font-semibold">
            {t("anagraphic.residenceTitle")}
          </h3>
        </div>

        {/* Dati di Residenza */}
        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.residenceCountry")}</Label>
          <div
            className={
              hasError("required_residence_country")
                ? "border border-red-500 rounded-md"
                : ""
            }
          >
            <CountrySelect
              disabled={loading}
              value={data.residenceCountry}
              apiEndpoint={`${environment.ELITE_BASE_URI}/geo/countries`}
              onValueChange={(value) => handleChange("residenceCountry", value)}
              placeholder={t("placeholders.residenceCountry")}
            />
          </div>
          {hasError("required_residence_country") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("required_residence_country")}
            </p>
          )}
        </div>

        {data.residenceCountry?.iso2?.toUpperCase() === "IT" && (
          <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
            <Label>{t("anagraphic.residenceProvince")}</Label>
            <div
              className={
                hasError("required_residence_province")
                  ? "border border-red-500 rounded-md"
                  : ""
              }
            >
              <ProvinceSelect
                disabled={loading}
                value={data.residenceProvince}
                onValueChange={(value) =>
                  handleChange("residenceProvince", value)
                }
                placeholder={t("placeholders.residenceProvince")}
              />
            </div>
            {hasError("required_residence_province") && (
              <p className="text-sm text-red-500">
                {getErrorMessage("required_residence_province")}
              </p>
            )}
          </div>
        )}

        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.residenceCity")}</Label>
          <div
            className={
              hasError("required_residence_city")
                ? "border border-red-500 rounded-md"
                : ""
            }
          >
            <CitySelect
              value={data.residenceCity}
              apiEndpoint={`${environment.ELITE_BASE_URI}/geo/cities`}
              disabled={loading || !data.residenceCountry?.id}
              nationId={data.residenceCountry?.id}
              onValueChange={(value) => handleChange("residenceCity", value)}
              placeholder={t("placeholders.residenceCity")}
            />
          </div>
          {hasError("required_residence_city") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("required_residence_city")}
            </p>
          )}
        </div>
        <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
          <Label>{t("anagraphic.residenceAddress")}</Label>
          <Input
            disabled={loading}
            value={data.residenceAddress}
            onChange={(e) => handleChange("residenceAddress", e.target.value)}
            placeholder={t("placeholders.residenceAddress")}
            className={
              hasError("required_residence_address") ? "border-red-500" : ""
            }
          />
          {hasError("required_residence_address") && (
            <p className="text-sm text-red-500">
              {getErrorMessage("required_residence_address")}
            </p>
          )}
        </div>

        {/* Campi aggiuntivi per residenza italiana */}
        {data.residenceCountry?.iso2?.toUpperCase() === "IT" && (
          <>
            <div className="col-span-2 mt-1 space-y-2 md:col-span-1">
              <Label>{t("anagraphic.postalCode")}</Label>
              <Input
                disabled={loading}
                value={data.cap}
                onChange={(e) => handleChange("cap", e.target.value)}
                placeholder={t("placeholders.postalCode")}
                className={
                  hasError("required_postal_code") ? "border-red-500" : ""
                }
              />
              {hasError("required_postal_code") && (
                <p className="text-sm text-red-500">
                  {getErrorMessage("required_postal_code")}
                </p>
              )}
            </div>
          </>
        )}
        {data.residenceCountry?.iso2?.toUpperCase() === "IT" && (
          <>
            <div className="col-span-2 mt-1 space-y-2">
              <Label>{t("anagraphic.taxCode")}</Label>
              <Input
                disabled={loading}
                value={data?.taxCode}
                maxLength={16}
                onChange={(e) =>
                  handleChange("taxCode", e.target.value.toUpperCase())
                }
                placeholder={t("placeholders.taxCode")}
              />
            </div>
          </>
        )}
      </div>

      {/* Pulsante Procedi alla Verifica */}
      <div className="col-span-2 mt-6 flex justify-end">
        <Button
          onClick={onNext}
          disabled={!isFormValid() || loading}
          className="flex items-center gap-2 w-full"
        >
          {t("anagraphic.proceedToVerify")}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </>
  );
}
